import { useEffect, useMemo, useRef, useState } from 'react';
import { useFetcher } from 'react-router-dom';
import {
  Combobox,
  ComboboxContent,
  ComboboxEmpty,
  ComboboxInput,
  ComboboxItem,
  ComboboxList,
} from '@/components/ui/combobox';
import type { Dealer, DealerId } from '@/models/entities/lead';
import { dealerService } from '@/services/dealerService.ts';

function dealerLabel(d: Dealer): string {
  return d.city ? `${d.name} - ${d.city}` : d.name;
}

export function DealerSelection() {
  const fetcher = useFetcher();
  const formRef = useRef<HTMLFormElement>(null);

  const availableDealers = useMemo(
    () => dealerService.getAvailableDealers(),
    [],
  );
  const availableIds = useMemo(
    () => Array.from(dealerService.getAvailableDealerIds()),
    [],
  );

  const labelById = useMemo(
    () => new Map(availableDealers.map((d: Dealer) => [d.id, dealerLabel(d)])),
    [availableDealers],
  );

  const [draftIds, setDraftIds] = useState<DealerId[]>(() => [
    ...dealerService.getSelectedDealerIds(),
  ]);

  const [open, setOpen] = useState(false);
  const prevOpenRef = useRef(false);
  const [query, setQuery] = useState('');

  const visibleIds = useMemo(() => {
    if (!query) return availableIds;
    const lowerQuery = query.toLowerCase();
    return availableIds.filter((id) => {
      const label = labelById.get(id) ?? '';
      return label.toLowerCase().includes(lowerQuery);
    });
  }, [query, availableIds, labelById]);

  const allVisibleSelected = useMemo(() => {
    return (
      visibleIds.length > 0 && visibleIds.every((id) => draftIds.includes(id))
    );
  }, [visibleIds, draftIds]);

  const onValueChange = (value: string | string[] | null) => {
    if (value === null) return;
    const ids = (Array.isArray(value) ? value : [value]) as DealerId[];
    if (ids.length === 0) return;
    setDraftIds(ids);
  };

  const onOpenChange = (
    nextOpen: boolean,
    eventDetails: { reason: string },
  ) => {
    if (nextOpen === false && eventDetails.reason === 'item-press') {
      return;
    }
    setOpen(nextOpen);
  };

  useEffect(() => {
    if (prevOpenRef.current === true && open === false) {
      const currentSelected = dealerService.getSelectedDealerIds();
      const hasChanged =
        draftIds.length !== currentSelected.size ||
        draftIds.some((id) => !currentSelected.has(id));

      if (hasChanged) {
        if (formRef.current) {
          fetcher.submit(formRef.current);
        }
      }
    }
    prevOpenRef.current = open;
  }, [open, draftIds, fetcher]);

  useEffect(() => {
    if (fetcher.state === 'idle') {
      setDraftIds([...dealerService.getSelectedDealerIds()]);
    }
  }, [fetcher.state]);

  const quickSelectLabel = allVisibleSelected ? 'Unselect all' : 'Select all';

  const onQuickSelect = () => {
    if (allVisibleSelected) {
      const nextIds = draftIds.filter((id) => !visibleIds.includes(id));
      if (nextIds.length === 0) {
        setDraftIds([availableIds[0]]);
      } else {
        setDraftIds(nextIds);
      }
    } else {
      const nextIds = Array.from(new Set([...draftIds, ...visibleIds]));
      setDraftIds(nextIds);
    }
  };

  const currentValue = draftIds;
  return (
    <div className="w-96">
      <fetcher.Form
        method="post"
        action="/leads"
        ref={formRef}
        style={{ display: 'none' }}
      >
        {draftIds.map((id) => (
          <input
            key={id}
            type="hidden"
            name="selectedDealerId"
            value={id}
            readOnly
          />
        ))}
      </fetcher.Form>
      <Combobox
        multiple
        open={open}
        value={currentValue}
        onValueChange={onValueChange}
        onOpenChange={onOpenChange}
        inputValue={query}
        onInputValueChange={setQuery}
        items={availableIds}
        filter={(itemValue, query) => {
          const label = labelById.get(itemValue as DealerId) ?? '';
          return label.toLowerCase().includes(query.toLowerCase());
        }}
      >
        <ComboboxInput
          placeholder="Select Dealers"
          className="bg-[#3d3d3d] text-white border-none h-8"
        />
        <ComboboxContent>
          <ComboboxEmpty>No dealer found</ComboboxEmpty>

          <button
            type="button"
            className="relative flex w-full cursor-default items-center gap-2 rounded-sm py-1.5 pr-8 pl-2 text-sm"
            onMouseDown={(e) => {
              e.preventDefault();
              onQuickSelect();
            }}
          >
            {quickSelectLabel}
          </button>
          <ComboboxList>
            {(dealerId: DealerId) => (
              <ComboboxItem key={dealerId} value={dealerId}>
                {labelById.get(dealerId) ?? dealerId}
              </ComboboxItem>
            )}
          </ComboboxList>
        </ComboboxContent>
      </Combobox>
    </div>
  );
}
